import { Component, OnInit, Input, Output, ElementRef, EventEmitter, ViewChildren, QueryList } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CfCoreComponent } from '../core/core.component';
import { CfInputComponent } from '../input/input.component';
import { CfGroupComponent } from '../group/group.component';
import { CfSelectComponent } from '../select/select.component';
import { GroupModel } from '../../models/group/group.model';
import { InputModel } from '../../models/input/input.model';
import { InputStylingModel } from '../../models/input/input-styling.model';
import { SelectModel } from '../../models/select/select.model';
import { SelectableModel } from '../../models/selectable/selectable.model';
import { SelectStylingModel } from '../../models/select/select-styling.model';
import { FormQuestionModel } from '../../models/form/form-question.model';
import { FormQuestionStylingModel } from '../../models/form/form-question-styling.model';
import { FormQuestionTemplates } from '../../templates/form-question.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>CF Form Question Component</p>
 * <pre>
 * <code>
 * import {{'{'}} FormQuestionModel {{'}'}} from 'cedrus-fusion'
 * import {{'{'}} FormQuestionStylingModel {{'}'}} from 'cedrus-fusion'
 * <code><</code>cf-form-question<code>></code><code><</code><code>/</code>cf-form-question<code>></code>
 * </code>
 * </pre>
 */
export class CfFormQuestionComponent extends CfCoreComponent implements OnInit {
/**
 * <p>Example:</p>
 * <code><pre>{{'{'}}
 *  questionID: '1',
 *  componentType: 'checkbox',
 *  required: true,
 *  key: 'status',
 *  value: 'single',
 *  label: 'Marital status',
 *  minSelected: 1,
 *  maxSelected: 2,
 *  items: [
 *    {{'{'}} label: 'Single', value: 'single' {{'}'}},
 *    {{'{'}} label: 'Married', value: 'married' {{'}'}},
 *    {{'{'}} label: 'Engaged', value: 'engaged' {{'}'}}
 *  ]
 *  details: {{'{'}} key: 'status', value: 'married;engaged' {{'}'}}
 * {{'}'}}</pre></code>
 * <h4>questionID: string</h4>
 * String question ID
 * <h4>componentType: string</h4>
 * Тape of the component by which the question should be displayed. Can be: input, checkbox, switch, radio, select.
 * <h4>required: boolean</h4>
 * Whether question is required
 * <h4>key: string</h4>
 * Question key
 * <h4>value: string</h4>
 * Question value
 * <h4>label: string</h4>
 * Question label
 * <h4>minSelected: number</h4>
 * Property used when componentType is: checkbox or switch
 * <h4>maxSelected: number</h4>
 * Property used when componentType is: checkbox or switch
 * <h4>items: array</h4>
 * Array with items for selection
 * <h4>validation: any</h4>
 * Validation settings
 * <h4>details: any</h4>
 * Question details
 */
public properties: FormQuestionModel;
/**
 * <p>Styling object:</p>
 * <h4>container: StylingModel</h4>
 * Styling properties for main container
 * <h4>errorMessage: StylingModel</h4>
 * Styling for the section with validation errors messages
 */
public styling: FormQuestionStylingModel;
/**
 * Question ID
 * @param {?} value
 * @return {?}
 */
set questionID(value: string) { this.properties.questionID = value }
/**
 * Тape of the component by which the question should be displayed. Can be: input, checkbox, switch, radio, select.
 * @param {?} value
 * @return {?}
 */
set componentType(value: string) { this.properties.componentType = value }
/**
 * Type of cf-input
 * @param {?} value
 * @return {?}
 */
set type(value: string) { this.properties.type = value }
/**
 * Whether question is required
 * @param {?} value
 * @return {?}
 */
set required(value: boolean) { this.properties.required = value }
/**
 * Question key
 * @param {?} value
 * @return {?}
 */
set key(value: string) { this.properties.key = value }
/**
 * Question value
 * @param {?} value
 * @return {?}
 */
set value(value: string) { this.properties.value = value }
/**
 * Question label
 * @param {?} value
 * @return {?}
 */
set label(value: string) { this.properties.label = value }
/**
 * Property used when componentType is: checkbox or switch.
 * @param {?} value
 * @return {?}
 */
set minSelected(value: number) { this.properties.minSelected = value }
/**
 * Property used when componentType is: checkbox, radio or switch.
 * @param {?} value
 * @return {?}
 */
set maxSelected(value: number) { this.properties.maxSelected = value }
/**
 * Array with items for selection
 * @param {?} value
 * @return {?}
 */
set items(value: any[]) { this.properties.items = value }
/**
 * Validation settings
 * @param {?} value
 * @return {?}
 */
set validation(value: any) { this.properties.validation = value }
/**
 * Question details
 * @param {?} value
 * @return {?}
 */
set details(value: any) { this.properties.details = value }
/**
 * @hidden
 */
form: FormGroup;
/**
 * @hidden
 * @return {?}
 */
get isValid() {
    if (this.properties.key != null && this.form != null && this.form.controls[this.properties.key] != null) {
      return this.form.controls[this.properties.key].valid || !this.form.controls[this.properties.key].dirty;
    } else {
      return true;
    }
  }
/**
 * @hidden
 */
inputs: QueryList<CfInputComponent>;
/**
 * @hidden
 */
groups: QueryList<CfGroupComponent>;
/**
 * @hidden
 */
selects: QueryList<CfSelectComponent>;
/**
 * @hidden
 */
private userValues: any[] = [];
/**
 * @hidden
 */
private dataForComponents: any = {};
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 */
constructor(public elementRef: ElementRef, /**@hidden */ templateService: TemplateService) {
    super(elementRef, templateService);
    this.properties = new FormQuestionModel();
    this.styling = new FormQuestionStylingModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit() {
    super.ngOnInit();
    this.getMyTemplate('form-question', FormQuestionTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'FORM-QUESTION').then(sampler => {
        this.styling = Object.assign({}, this.activeTemplate.style, this.styling);
        let /** @type {?} */ addAsterix = '';
        if (this.properties['required'] != null && this.properties['required'] === true) { addAsterix = '*'; }
        this.populateProperties(addAsterix);
        if (this.form == null) {
          let /** @type {?} */ groupName = {};
          groupName[this.properties.key] = new FormControl(this.properties.value || '', null);
          this.form = new FormGroup(groupName);
        }
        // console.log('FORM-QUESTION', this);
      });
    });
  }
/**
 * @hidden 
 * Prepare data according to the value of componentType
 * @param {?} addAsterix
 * @return {?}
 */
populateProperties(addAsterix) {
    switch (this.properties['componentType']) {
      case 'input':
        {
          this.dataForComponents = {
            properties: new InputModel({
              type: this.properties['type'],
              placeholder: this.properties['label'] + addAsterix,
              value: this.properties['value']
            })
          }
          break;
        }
      case 'checkbox':
      case 'switch':
      case 'radio':
        {
          this.dataForComponents = [];
          this.properties['label'] = this.properties['label'] + addAsterix;
          for (let /** @type {?} */ item of this.properties.items) {
            let /** @type {?} */ chec = new SelectableModel({ item: item['label'], value: item['value'] });
            this.dataForComponents.push(chec);
          }
          break;
        }
      case 'select':
        {
          this.dataForComponents = {
            properties: new SelectModel({
              placeholder: this.properties['label'] + addAsterix,
              options: this.properties.items,
              optionsSource: 'fromModel',
              selected: this.properties['value'],
              showFilter: false
            })
          }
          break;
        }
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-form-question',
  template: "<div *ngIf=\"_inited && properties?.display\" [formGroup]=\"form\" class=\"cf-form-question-container {{styling?.container?.class}}\" [ngClass]=\"getDynamicClasses(styling?.container?.dynamicClass)\"> <div [ngSwitch]=\"properties?.componentType\" class=\"question-component-type\"> <cf-input [id]=\"properties?.key\" *ngSwitchCase=\"'input'\" [formControlName]=\"properties?.key\" [properties]=\"dataForComponents?.properties\" [styling]=\"dataForComponents?.styling\"></cf-input> <cf-group [id]=\"properties?.key\" *ngSwitchCase=\"'checkbox'\" [formControlName]=\"properties?.key\" [label]=\"properties?.label\" [minSelected]=\"properties?.minSelected\" [maxSelected]=\"properties?.maxSelected\"> <cf-checkbox *ngFor=\"let item of dataForComponents\" [properties]=\"item\"></cf-checkbox> </cf-group> <cf-group [id]=\"properties?.key\" *ngSwitchCase=\"'switch'\" [formControlName]=\"properties?.key\" [label]=\"properties?.label\" [minSelected]=\"properties?.minSelected\" [maxSelected]=\"properties?.maxSelected\"> <cf-switch *ngFor=\"let item of dataForComponents\" [properties]=\"item\"></cf-switch> </cf-group> <cf-group [id]=\"properties?.key\" *ngSwitchCase=\"'radio'\" [formControlName]=\"properties?.key\" [label]=\"properties?.label\" minSelected=\"1\" maxSelected=\"1\"> <cf-radio *ngFor=\"let item of dataForComponents\" [properties]=\"item\"></cf-radio> </cf-group> <cf-select [id]=\"properties?.key\" *ngSwitchCase=\"'select'\" [formControlName]=\"properties?.key\" [properties]=\"dataForComponents?.properties\" [styling]=\"dataForComponents?.styling\"></cf-select> <div *ngSwitchCase=\"'empty'\"></div> </div> <div class=\"error-message {{styling?.errorMessage?.class}}\" [ngClass]=\"getDynamicClasses(styling?.errorMessage?.dynamicClass)\"> <span *ngIf=\"form?.controls[properties?.key] && form?.controls[properties?.key]?.hasError('required') && form?.controls[properties?.key]?.dirty\">required</span> <span *ngIf=\"!isValid && properties?.validation!=null\">{{properties?.validation?.message}}</span> </div> </div>",
  styles: [".question-component-type { display: inline-block; width: 100%; } cf-checkbox, cf-switch, cf-radio, cf-input, cf-select { display: block; width: 100%; } cf-checkbox ::ng-deep > div, cf-switch ::ng-deep > div, cf-radio ::ng-deep > div, cf-input ::ng-deep > div, cf-select ::ng-deep > div { width: 100%; } cf-checkbox ::ng-deep .mat-input-container, cf-checkbox ::ng-deep .mat-select, cf-switch ::ng-deep .mat-input-container, cf-switch ::ng-deep .mat-select, cf-radio ::ng-deep .mat-input-container, cf-radio ::ng-deep .mat-select, cf-input ::ng-deep .mat-input-container, cf-input ::ng-deep .mat-select, cf-select ::ng-deep .mat-input-container, cf-select ::ng-deep .mat-select { font-size: 14px; width: 100%; } cf-checkbox ::ng-deep .mat-select-trigger, cf-switch ::ng-deep .mat-select-trigger, cf-radio ::ng-deep .mat-select-trigger, cf-input ::ng-deep .mat-select-trigger, cf-select ::ng-deep .mat-select-trigger { font-size: 14px; } .error-message { font-family: 'Avenir'; color: red; font-size: 10px; display: inline-block; } [hidden] { display: none !important; } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'questionID': [{ type: Input, args: ['questionID', ] },],
'componentType': [{ type: Input, args: ['componentType', ] },],
'type': [{ type: Input, args: ['type', ] },],
'required': [{ type: Input, args: ['required', ] },],
'key': [{ type: Input, args: ['key', ] },],
'value': [{ type: Input, args: ['value', ] },],
'label': [{ type: Input, args: ['label', ] },],
'minSelected': [{ type: Input, args: ['minSelected', ] },],
'maxSelected': [{ type: Input, args: ['maxSelected', ] },],
'items': [{ type: Input, args: ['items', ] },],
'validation': [{ type: Input, args: ['validation', ] },],
'details': [{ type: Input, args: ['details', ] },],
'form': [{ type: Input },],
'inputs': [{ type: ViewChildren, args: [CfInputComponent, ] },],
'groups': [{ type: ViewChildren, args: [CfGroupComponent, ] },],
'selects': [{ type: ViewChildren, args: [CfSelectComponent, ] },],
};
}

function CfFormQuestionComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfFormQuestionComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfFormQuestionComponent.ctorParameters;
/** @type {?} */
CfFormQuestionComponent.propDecorators;
/**
 * <p>Example:</p>
 * <code><pre>{{'{'}}
 *  questionID: '1',
 *  componentType: 'checkbox',
 *  required: true,
 *  key: 'status',
 *  value: 'single',
 *  label: 'Marital status',
 *  minSelected: 1,
 *  maxSelected: 2,
 *  items: [
 *    {{'{'}} label: 'Single', value: 'single' {{'}'}},
 *    {{'{'}} label: 'Married', value: 'married' {{'}'}},
 *    {{'{'}} label: 'Engaged', value: 'engaged' {{'}'}}
 *  ]
 *  details: {{'{'}} key: 'status', value: 'married;engaged' {{'}'}}
 * {{'}'}}</pre></code>
 * <h4>questionID: string</h4>
 * String question ID
 * <h4>componentType: string</h4>
 * Тape of the component by which the question should be displayed. Can be: input, checkbox, switch, radio, select.
 * <h4>required: boolean</h4>
 * Whether question is required
 * <h4>key: string</h4>
 * Question key
 * <h4>value: string</h4>
 * Question value
 * <h4>label: string</h4>
 * Question label
 * <h4>minSelected: number</h4>
 * Property used when componentType is: checkbox or switch
 * <h4>maxSelected: number</h4>
 * Property used when componentType is: checkbox or switch
 * <h4>items: array</h4>
 * Array with items for selection
 * <h4>validation: any</h4>
 * Validation settings
 * <h4>details: any</h4>
 * Question details
 * @type {?}
 */
CfFormQuestionComponent.prototype.properties;
/**
 * <p>Styling object:</p>
 * <h4>container: StylingModel</h4>
 * Styling properties for main container
 * <h4>errorMessage: StylingModel</h4>
 * Styling for the section with validation errors messages
 * @type {?}
 */
CfFormQuestionComponent.prototype.styling;
/**
 * @hidden
 * @type {?}
 */
CfFormQuestionComponent.prototype.form;
/**
 * @hidden
 * @type {?}
 */
CfFormQuestionComponent.prototype.inputs;
/**
 * @hidden
 * @type {?}
 */
CfFormQuestionComponent.prototype.groups;
/**
 * @hidden
 * @type {?}
 */
CfFormQuestionComponent.prototype.selects;
/**
 * @hidden
 * @type {?}
 */
CfFormQuestionComponent.prototype.userValues;
/**
 * @hidden
 * @type {?}
 */
CfFormQuestionComponent.prototype.dataForComponents;
/** @type {?} */
CfFormQuestionComponent.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
